import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import { InputGroupContext } from '../input-group/utils';
import type { TrackingProp } from '../types';
import {
  anchorPosition,
  getLabel,
  getText,
  useDescribedby,
  useId,
  useTracking
} from '../utils';
import styles from './validation-message.module.css';
import clsx from 'clsx';

export type ValidationMessageProps = React.ComponentPropsWithoutRef<'span'> & {
  htmlFor?: string;
  invalid?: boolean;
  tracking?: TrackingProp;
  internal?: 'small' | 'table-inline';
};

export const ValidationMessage = forwardRef<
  HTMLSpanElement,
  ValidationMessageProps
>(function ValidationMessage(
  {
    className,
    htmlFor,
    id,
    internal,
    invalid = true,
    tracking = true,
    ...rest
  },
  ref
) {
  const ariaId = useId();
  const isTracked = useRef(false);
  const innerRef = useRef<HTMLSpanElement>(null);
  const track = useTracking(tracking);
  const connectWithInput = useCallback(
    (input: Element) => {
      const align = window.getComputedStyle(input).textAlign;
      const inputWrapper = input.parentElement!;
      const self = innerRef.current;
      const isAnchor = !!self && !!internal;
      const isEmpty = !self?.textContent?.trim();
      const unAnchor = isAnchor && anchorPosition(inputWrapper, self, align);
      if (isEmpty) input.removeAttribute('aria-invalid');
      else input.setAttribute('aria-invalid', `${invalid}`);

      return () => {
        input.removeAttribute('aria-invalid');
        if (unAnchor) unAnchor();
      };
    },
    [internal, invalid]
  );

  // Use htmlFor or input names as selector
  const restClone = { ...rest, name: '' };
  useContext(InputGroupContext)(restClone); // Augment rest clone with potential props from InputGroup
  const selector = htmlFor ? `[id="${htmlFor}"]` : `[name="${restClone.name}"]`; // Use name as selector if multiple inputs, such as InputGroup

  useImperativeHandle(ref, () => innerRef.current as HTMLSpanElement); // Forward innerRef
  useDescribedby(
    innerRef,
    !rest.hidden && !!rest.children && selector, // Ensure that connectWithInput is called if children changes
    connectWithInput,
    'first'
  ); // Always up ValidationMessage as first in aria-describedby

  // Track first impression if neither hidden nor empty
  useEffect(() => {
    const isEmpty = !innerRef.current?.textContent?.trim();
    if (isTracked.current || isEmpty || rest.hidden) return;
    const fieldset = innerRef.current?.closest('fieldset');
    const legend = fieldset?.querySelector('legend');
    const target = document.getElementById(htmlFor || '-') || legend;
    isTracked.current = true;

    track({
      eventCategory: `validationmessage_${invalid ? 'invalid' : 'success'}`,
      eventAction: 'impression',
      eventLabel: `validationmessage_${getLabel(target)}`,
      eventStatus: getText(innerRef.current)
    });
  });

  return (
    <span
      aria-live="polite"
      className={clsx(
        styles.message,
        className,
        !invalid && styles.ok,
        !!internal && styles[internal]
      )}
      id={id || ariaId}
      ref={innerRef}
      {...rest}
    />
  );
});
